import { useState, useEffect } from "react";
import { Routes, Route, Link, Navigate, NavLink, useLocation } from "react-router-dom";
import { SignIn, SignUp, SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { AuthArea } from "./components/AuthArea";
import { Protected } from "./components/Protected";
import { Mark } from "./lib/mark";
import { Home } from "./routes/Home";
import { Basics } from "./routes/Basics";
import { RoomShell, ComingPanel } from "./rooms/RoomShell";
import { PokerPlay } from "./rooms/PokerPlay";
import { PokerTrain } from "./rooms/PokerTrain";
import { TrainRun } from "./rooms/TrainRun";
import { CustomTable } from "./rooms/CustomTable";
import { CompetePanel } from "./rooms/Panels";
import { LearnTab } from "./learn/LearnTab";
import { LessonPage } from "./learn/LessonPage";
import { BlackjackPlay, BlackjackTable, BlackjackTrain } from "./rooms/BlackjackRoom";
import { BlackjackTrainRun } from "./rooms/BlackjackDrills";
import { resolveSeat } from "./rooms/seat-url";
import "./rooms/rooms.css";
import { Account } from "./routes/Account";
import { Pricing } from "./routes/Pricing";
import { Onboarding } from "./routes/Onboarding";
import { Play } from "./routes/Play";
import { LeakReport } from "./routes/LeakReport";
import { Fairness } from "./routes/Fairness";
import { Terms, Privacy, Refunds, ResponsiblePlay } from "./routes/Legal";
import { About } from "./routes/About";
import { Admin } from "./routes/Admin";
import { MeProvider, useMeContext } from "./lib/MeContext";
import { WalletProvider } from "./lib/WalletContext";
import { Card, Skeleton } from "./components/ui";
import { clerkAppearance } from "./lib/clerkTheme";

/** Asks the profile question once after signup, then never again. */
function Welcome() {
  const { me, loading, refresh } = useMeContext();
  const [done, setDone] = useState(false);

  if (loading && !me) return <Card><Skeleton lines={4} /></Card>;
  if (!me) return <Navigate to="/sign-in" replace />;
  if (done || me.user.playsWhere) return <Navigate to="/account" replace />;

  return <Onboarding onDone={() => { setDone(true); void refresh(); }} />;
}

/**
 * The document title, per route.
 *
 * A client-rendered SPA keeps whatever title the shell shipped with unless something
 * changes it, so every page was called "BluffBusters" — in the tab, in a bookmark,
 * and in the browser history, which makes a back button through the app unreadable.
 *
 * Route titles only, not descriptions: the meta description in index.html is what a
 * crawler reads, and a crawler never runs this.
 */
const TITLES: Record<string, string> = {
  "/": "BluffBusters — the saloon",
  "/pricing": "Pricing — BluffBusters",
  "/about": "About — BluffBusters",
  "/fair": "The shuffle — BluffBusters",
  "/terms": "Terms of Service — BluffBusters",
  "/privacy": "Privacy Policy — BluffBusters",
  "/refunds": "Refund Policy — BluffBusters",
  "/responsible-play": "Responsible play — BluffBusters",
  "/train": "Trainer — BluffBusters",
  "/play": "Play — BluffBusters",
  "/basics": "How to play — BluffBusters",
  /* Phase 9: a lesson page sets its OWN title (the lesson's), so this names only the
     bare path; `useRouteTitle` leaves `/learn/<id>` alone. */
  "/learn": "Learn — BluffBusters",
  /*
   * The rooms. `useRouteTitle` matches the LONGEST key that the path starts with, so a
   * room's own entry covers its tabs and only the tabs worth their own name in a
   * browser history get one — the table and the trainer, which are where somebody
   * actually is when they hit the back button.
   */
  "/poker": "The poker room — BluffBusters",
  "/poker/play": "Play — BluffBusters",
  "/poker/play/custom": "Custom table — BluffBusters",
  "/poker/play/table": "At the table — BluffBusters",
  "/poker/train": "Train — BluffBusters",
  "/poker/train/run": "Drill — BluffBusters",
  "/poker/train/build": "Build a drill — BluffBusters",
  "/poker/learn": "Learn — BluffBusters",
  "/poker/compete": "Compete — BluffBusters",
  "/blackjack": "The blackjack room — BluffBusters",
  "/blackjack/play": "Blackjack — BluffBusters",
  "/blackjack/play/table": "At the blackjack table — BluffBusters",
  "/blackjack/train": "Blackjack training — BluffBusters",
  "/blackjack/train/run": "Blackjack drill — BluffBusters",
  "/blackjack/learn": "Blackjack — Learn — BluffBusters",
  "/blackjack/compete": "Blackjack — Compete — BluffBusters",
  "/cribbage": "The cribbage room — BluffBusters",
  "/cribbage/play": "Cribbage — BluffBusters",
  "/cribbage/train": "Cribbage training — BluffBusters",
  "/cribbage/learn": "Cribbage — Learn — BluffBusters",
  "/cribbage/compete": "Cribbage — Compete — BluffBusters",
  "/report": "Leak report — BluffBusters",
  "/account": "Account — BluffBusters",
  "/admin": "Admin — BluffBusters",
  "/welcome": "Welcome — BluffBusters",
  "/sign-in": "Sign in — BluffBusters",
  "/sign-up": "Create an account — BluffBusters",
};

function useRouteTitle(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    /*
     * The LONGEST matching key, not the first one.
     *
     * Clerk mounts its own sub-routes under `/sign-in/*` and `/sign-up/*`, which is why
     * a prefix match is needed at all. Phase 2b made the prefixes nest — `/poker`,
     * `/poker/play`, `/poker/play/table` — and `find` returns whichever comes first in
     * the object, so every tab in the poker room was titled "The poker room". Sorting by
     * length is the whole fix, and it is what the entries above already assume.
     */
    /* Phase 9: a lesson's page titles itself after its lesson (`LessonPage`), and this
       effect runs after the child's — so it steps aside there rather than overwrite it. */
    if (pathname.startsWith("/learn/")) return;
    const key = Object.keys(TITLES)
      .filter((k) => k === pathname || (k !== "/" && pathname.startsWith(k + "/")))
      .sort((a, b) => b.length - a.length)[0];
    document.title = (key && TITLES[key]) || "BluffBusters";
  }, [pathname]);
}

/** The admin link, shown only to owners so the nav is not littered with a 403. */
function AdminLink() {
  const { me } = useMeContext();
  if (me?.user.role !== "admin") return null;
  return <NavLink to="/admin">Admin</NavLink>;
}

/**
 * The seat, resolved from the URL.
 *
 * `resolveSeat` reads the card and its dials (or a whole custom setup) out of the query
 * and adds the player's own Level 3 values, which are never in the link. The route
 * component is this thin on purpose: `Play` should not know how a seat is addressed,
 * and the URL should not know what a `Setup` is.
 */
function PokerSeat() {
  const { search } = useLocation();
  const seat = resolveSeat(search);
  return <Play seat={seat} />;
}

function Shell() {
  useRouteTitle();
  /**
   * The trainer gets a wider column than the rest of the site.
   *
   * `.shell` is 46rem, which is a reading width and is right for every other page.
   * The trainer is a workspace — a drill list, a felt and a range grid side by side —
   * and three columns inside 46rem is three columns nobody can read. Widened here
   * rather than by the route, because the constraint lives on the shell: `.play` has
   * carried `max-width: 68rem` for months and it has never once applied, since a
   * child cannot be wider than the parent that clips it.
   */
  const { pathname } = useLocation();
  const wide = pathname === "/train" || pathname.startsWith("/train/");
  /* Declared once and placed twice: inside the site shell, or alone. */
  const routes = (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* Public on purpose: a fairness claim nobody can read is not a claim. */}
          <Route path="/fair" element={<Fairness />} />
          <Route path="/about" element={<About />} />
          {/* Legal has to be reachable without an account — Stripe links to it, and
              somebody deciding whether to trust us has not signed up yet. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/responsible-play" element={<ResponsiblePlay />} />
          <Route
            path="/sign-in/*"
            element={
              <div className="center">
                <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up"
                        forceRedirectUrl="/welcome" appearance={clerkAppearance} />
              </div>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <div className="center">
                <SignUp routing="path" path="/sign-up" signInUrl="/sign-in"
                        forceRedirectUrl="/welcome" appearance={clerkAppearance} />
              </div>
            }
          />
          {/*
            * Absolute paths on the children of the room shells, not relative ones.
            *
            * React Router is happy either way as long as a child extends its parent.
            * `tests/site-meta.test.ts` reads every `<Route path>` in this file and
            * checks it against the titles, the sitemap and robots.txt — four
            * hand-written lists that have to agree — and a relative `path="play"` is
            * not a URL any of those three can be asked about. Writing them out keeps
            * the guard able to do its job, and reads better here too.
            */}
          <Route path="/basics" element={<Basics />} />
          {/* Phase 9: the Learn library's public pages. One route; the sitemap lists
              every lesson (written by `scripts/build-learn.ts`), and `/learn` itself
              goes to the shelves, which live in the poker room's Learn tab. */}
          <Route path="/learn" element={<Navigate to="/poker/learn" replace />} />
          <Route path="/learn/:id" element={<LessonPage />} />
          <Route path="/basics/:step" element={<Basics />} />

          {/*
            * ===================== PHASE 2B: THE ROOMS =============================
            * Three rooms, each a route with the same four tabs, and the tab in the URL
            * so it can be linked to and survives a reload.
            *
            * THE GATE IS AT THE SEAT, NOT AT THE DOOR. A visitor can open any room,
            * read the cards, look at the Learn outline and see what the place is; what
            * needs an account is sitting down (so the session has somewhere to be
            * saved) and the trainer (which spends a metered allowance). That is why
            * `<Protected>` is on the table and the drill and on nothing else here.
            * =====================================================================
            */}
          <Route path="/poker" element={<RoomShell roomId="poker" />}>
            <Route index element={<Navigate to="/poker/play" replace />} />
            <Route path="/poker/play" element={<PokerPlay />} />
            <Route path="/poker/play/custom" element={<CustomTable />} />
            <Route path="/poker/train" element={<PokerTrain />} />
            <Route path="/poker/train/run" element={<Protected><TrainRun /></Protected>} />
            <Route path="/poker/train/build" element={<Protected><TrainRun build /></Protected>} />
            <Route path="/poker/learn" element={<LearnTab />} />
            <Route path="/poker/compete" element={<CompetePanel />} />
          </Route>
          {/*
            The table is NOT inside the shell: it has its own header (Leave and the
            units toggle instead of tabs) and it is the whole viewport. The game runs
            entirely in the browser, so it needs no entitlement check and costs nothing
            to serve; it is behind sign-in only so that a session has somewhere to be
            saved.
          */}
          <Route path="/poker/play/table" element={<Protected><PokerSeat /></Protected>} />

          {/*
            * FREE FOR EVERY TIER, and deliberately not wrapped in <Protected>.
            *
            * The Blackjack Room carries no prizes, no tournament, no leaderboard and
            * nothing purchasable. It is a practice room and a reason to stay on the
            * site, not a product line — so it is not gated, not metered, and not
            * counted against anyone's daily spots. If a future change wraps this in a
            * plan gate, that is a product decision and not a tidy-up.
            */}
          <Route path="/blackjack" element={<RoomShell roomId="blackjack" />}>
            <Route index element={<Navigate to="/blackjack/play" replace />} />
            <Route path="/blackjack/play" element={<BlackjackPlay />} />
            <Route path="/blackjack/train" element={<BlackjackTrain />} />
            {/* Phase 8: the three drills, inside the room's tabs like poker's. */}
            <Route path="/blackjack/train/run" element={<BlackjackTrainRun />} />
            <Route path="/blackjack/learn" element={<LearnTab />} />
            <Route path="/blackjack/compete" element={<CompetePanel />} />
          </Route>

          {/* Phase 8: the table is a full screen of its own, like poker's — and, like the
              room, not behind sign-in. */}
          <Route path="/blackjack/play/table" element={<BlackjackTable />} />

          <Route path="/cribbage" element={<RoomShell roomId="cribbage" />}>
            <Route index element={<Navigate to="/cribbage/play" replace />} />
            <Route path="/cribbage/play" element={<ComingPanel title="Coming" lines={[
              "Six cards, a crib, and pegging to 121. The spec is written and the kit — cards, wallet, Ledger, Director — is most of what it needs.",
              "It comes after the blackjack room is re-based on the kit.",
            ]} />} />
            <Route path="/cribbage/train" element={<ComingPanel title="Coming" lines={[
              "A discard analyzer (six choose four against the crib) and a pegging drill.",
            ]} />} />
            <Route path="/cribbage/learn" element={<ComingPanel title="Coming" lines={[
              "The rules, the counting, and why fifteen-two is worth knowing cold.",
            ]} />} />
            <Route path="/cribbage/compete" element={<ComingPanel title="Coming" lines={[
              "A daily hand and contests, on their own board.",
            ]} />} />
          </Route>

          {/* The old flat routes. Bookmarks keep working; the rooms are the product. */}
          <Route path="/train" element={<Navigate to="/poker/train" replace />} />
          <Route path="/play" element={<Navigate to="/poker/play" replace />} />
          <Route path="/report" element={<Protected><LeakReport /></Protected>} />
          <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          {/* Gated again in the Worker by requireAdmin; this only hides the screen. */}
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route
            path="*"
            element={
              <Card>
                <h1>Not found</h1>
                <p className="muted">That page doesn't exist.</p>
                <Link className="btn-quiet" to="/">Back to the start</Link>
              </Card>
            }
          />
        </Routes>
  );

  /*
   * ===================== PHASE 2B: SCREENS WITHOUT THE SITE ======================
   * The saloon and the three rooms carry their own navigation — the saloon's doors and
   * footer strip, the room header's back link and gear — and they are SCREENS rather
   * than documents: the saloon is one screen, and the table screen is exactly the
   * viewport. Rendering the site's nav and footer around them would put 265px of page
   * chrome above a felt that has to fit a phone, which is the measured bug box 4 of
   * this phase exists to fix.
   *
   * So these routes render alone. Everything the nav carried is on the saloon's footer
   * strip, one tap away through the room header's back link.
   * ===============================================================================
   */
  const chromeless = pathname === "/" || /^\/(poker|blackjack|cribbage)(\/|$)/.test(pathname);
  if (chromeless) return <div className="shell-bare">{routes}</div>;
  return (
    <div className={`shell${wide ? " shell-wide" : ""}`}>
      <header className="nav">
        <Link className="brand" to="/">
          <Mark className="brand-mark" title="BluffBusters" />
          Bluff<span>Busters</span>
        </Link>
        <nav>
          {/* Pricing never depends on auth, so it stays put while Clerk boots. */}
          <NavLink to="/pricing">Pricing</NavLink>
          <AuthArea placeholder={<span className="nav-placeholder" aria-hidden="true" />}>
            <SignedIn>
              {/* The rooms, not the old flat routes. `/train` and `/play` still
                  work — they redirect — so a bookmark is not broken. */}
              <NavLink to="/poker/train">Train</NavLink>
              <NavLink to="/poker/play">Play</NavLink>
              <NavLink to="/blackjack">Blackjack</NavLink>
              <NavLink to="/report">Report</NavLink>
              <NavLink to="/account">Account</NavLink>
              {/* Only owners see the link. The Worker refuses everyone else anyway. */}
              <AdminLink />
              <UserButton afterSignOutUrl="/" appearance={clerkAppearance} />
            </SignedIn>
            <SignedOut>
              {/*
                * The blackjack room is in the signed-out nav on purpose.
                *
                * It is free for everyone and needs no account, and it is the only page
                * on this site that demonstrates the analysis rather than describing it.
                * A visitor who can only read about exact per-decision numbers on the
                * pricing page has been told; a visitor who can go and watch them
                * appear has been shown.
                */}
              <NavLink to="/blackjack">Blackjack</NavLink>
              <Link className="btn-small" to="/sign-in">Sign in</Link>
            </SignedOut>
          </AuthArea>
        </nav>
      </header>

      <main>{routes}</main>

      <footer>
        <span className="muted small">
          BluffBusters · exact short-stack tournament training
        </span>
        {/*
          The shuffle page has to be reachable without an account. A player deciding
          whether to trust the deck is often a player who has not signed up yet, and a
          fairness claim only findable from inside the product is decoration.
        */}
        <nav className="foot-links muted small">
          <Link to="/fair">The shuffle</Link>
          <Link to="/about">About</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/refunds">Refunds</Link>
          <Link to="/responsible-play">Responsible play</Link>
        </nav>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <MeProvider>
      {/* The wallet sits inside `MeProvider` and outside every route: the saloon
          header, each room header and the felt's gauge all read the same balance, and
          a screen where two of them disagree is a screen nobody trusts about money. */}
      <WalletProvider>
      <Shell />
      </WalletProvider>
    </MeProvider>
  );
}
